// src/hooks/useAuth.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { clearAuthCaches } from '@/lib/cacheManager';
import { useLanguage } from '@/components/LanguageContext';
import { useAuth } from '@/lib/AuthContext';

// Helper to check if error is auth-related
const isAuthError = (error) => {
  if (!error) return false;
  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toLowerCase() || '';
  return (
    message.includes('jwt') ||
    message.includes('token') ||
    message.includes('session') ||
    message.includes('unauthorized') ||
    message.includes('401') ||
    message.includes('refresh') ||
    code === 'pgrst301' ||
    code === '401'
  );
};

// =====================================================
// AUTH QUERIES
// =====================================================

/**
 * Get current user with profile data.
 * Only runs when AuthContext has already authenticated (avoids clearing session during initial load / on login page).
 */
export const useCurrentUser = (options = {}) => {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['auth', 'current-user'],
    enabled: !!isAuthenticated,
    queryFn: async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();

        if (authError) {
          // If auth error, try to refresh the session first
          if (isAuthError(authError)) {
            logger.debug('Auth error detected, attempting session refresh...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError || !refreshData.session) {
              logger.error('Session refresh failed, clearing auth state');
              clearAuthCaches();
              // Return null instead of throwing to trigger redirect gracefully
              return null;
            }

            // Retry getUser after refresh
            const { data: { user: refreshedUser }, error: retryError } = await supabase.auth.getUser();
            if (retryError || !refreshedUser) {
              logger.error('Still cannot get user after refresh');
              clearAuthCaches();
              return null;
            }

            // Continue with refreshed user
            const { data: profile, error: profileError } = await supabase
              .from('app_users')
              .select('*')
              .eq('id', refreshedUser.id)
              .single();

            if (profileError) {
              if (isAuthError(profileError)) {
                clearAuthCaches();
                return null;
              }
              throw profileError;
            }

            return {
              ...refreshedUser,
              profile: {
                ...profile,
                roles: profile.roles || [],
                primary_role: profile.primary_role,
                active_role: profile.active_role || profile.primary_role
              }
            };
          }
          throw authError;
        }

        if (!user) return null;

        // Get user profile with multi-role support
        const { data: profile, error: profileError } = await supabase
          .from('app_users')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          // Handle auth errors on profile fetch
          if (isAuthError(profileError)) {
            logger.error('Auth error fetching profile, clearing session');
            clearAuthCaches();
            return null;
          }
          throw profileError;
        }

        return {
          ...user,
          profile: {
            ...profile,
            // Ensure roles array exists
            roles: profile.roles || [],
            primary_role: profile.primary_role,
            active_role: profile.active_role || profile.primary_role
          }
        };
      } catch (error) {
        // Catch-all for auth errors
        if (isAuthError(error)) {
          logger.error('Unhandled auth error in useCurrentUser:', error.message);
          clearAuthCaches();
          return null;
        }
        throw error;
      }
    },
    staleTime: 1000 * 60 * 2, // Reduced to 2 minutes for more frequent validation
    retry: (failureCount, error) => {
      // Don't retry auth errors - they need session refresh, not retries
      if (isAuthError(error)) return false;
      return failureCount < 2;
    },
    ...options
  });
};

/**
 * Get user's active role
 */
export const useActiveRole = () => {
  const { data: user } = useCurrentUser();
  return user?.profile?.active_role || 'parent';
};

/**
 * Check if user has specific role
 */
export const useHasRole = (role) => {
  const { data: user } = useCurrentUser();
  return user?.profile?.roles?.includes(role) || false;
};

/**
 * Check if user has multiple roles
 */
export const useHasMultipleRoles = () => {
  const { data: user } = useCurrentUser();
  return (user?.profile?.roles?.length || 0) > 1;
};

/**
 * Branches where the user can act as teacher (main branch + admin "also teacher" branches)
 */
export const getTeacherBranchIds = (profile) => {
  if (!profile) return [];
  const roles = profile.roles || [];
  const alsoTeacher = profile.metadata?.also_teacher_branch_ids;
  const alsoArr = Array.isArray(alsoTeacher) ? alsoTeacher : [];
  const ids = [];
  if (roles.includes('teacher') && profile.current_branch_id) ids.push(profile.current_branch_id);
  ids.push(...alsoArr);
  return [...new Set(ids)].filter(Boolean);
};

/**
 * True when context switcher should be shown: multiple roles OR branch_admin with multiple branches OR teacher in multiple branches
 */
export const useHasMultipleContexts = () => {
  const { data: user } = useCurrentUser();
  const profile = user?.profile;
  const roles = profile?.roles || [];
  const branchIds = profile?.branch_ids || [];
  const teacherBranchIds = getTeacherBranchIds(profile);
  return (
    roles.length > 1 ||
    (roles.includes('branch_admin') && branchIds.length > 1) ||
    (roles.includes('teacher') && teacherBranchIds.length > 1)
  );
};

// =====================================================
// AUTH MUTATIONS
// =====================================================

/** Role home paths for client-side nav after role switch (avoids full reload / "odzyskiwanie sesji"). */
export const getRoleHomePath = (activeRole) => {
  const paths = {
    parent: '/Home',
    teacher: '/TeacherDashboard',
    branch_admin: '/Home',
    super_admin: '/PlatformDashboard'
  };
  return paths[activeRole] || '/Home';
};

/**
 * Switch active role
 */
const SWITCHED_TO_KEYS = {
  parent: 'switchedToParentView',
  teacher: 'switchedToTeacherView',
  branch_admin: 'switchedToBranchAdminView',
  super_admin: 'switchedToSuperAdminView'
};

/**
 * Switch role (and optionally branch for branch_admin).
 * @param {string|{ role: string, branchId?: string }} payload - Role string or { role, branchId } for branch_admin + branch
 */
export const useSwitchRole = () => {
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async (payload) => {
      const newRole = typeof payload === 'string' ? payload : payload?.role;
      const branchId = typeof payload === 'object' ? payload?.branchId : undefined;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user logged in');

      // Verify user has this role and branch access when branchId is provided
      const { data: profile } = await supabase
        .from('app_users')
        .select('roles, branch_ids, current_branch_id, metadata')
        .eq('id', user.id)
        .single();

      if (!profile?.roles?.includes(newRole)) {
        throw new Error(`User does not have ${newRole} role`);
      }

      if (newRole === 'branch_admin' && branchId && !profile?.branch_ids?.includes(branchId)) {
        throw new Error('User is not admin of this branch');
      }

      if (newRole === 'teacher' && branchId) {
        const alsoTeacherIds = profile?.metadata?.also_teacher_branch_ids;
        const alsoTeacherArr = Array.isArray(alsoTeacherIds) ? alsoTeacherIds : [];
        const isTeacherInBranch =
          profile?.current_branch_id === branchId ||
          alsoTeacherArr.includes(branchId);
        if (!isTeacherInBranch) {
          throw new Error('User is not a teacher in this branch');
        }
      }

      const updates = { active_role: newRole };
      if (branchId && (newRole === 'branch_admin' || newRole === 'teacher')) {
        updates.current_branch_id = branchId;
      }

      const { data, error } = await supabase
        .from('app_users')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      const messageKey = SWITCHED_TO_KEYS[data.active_role];
      toast.success(messageKey ? t(messageKey) : `Switched to ${data.active_role} view`);
    },
    onError: (error) => {
      logger.error('Switch role error', { message: error.message });
      toast.error(`Failed to switch role: ${error.message}`);
    }
  });
};

/**
 * Update user profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, ...updates }) => {
      const { data, error } = await supabase
        .from('app_users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      toast.success('Profile updated successfully!');
    },
    onError: (error) => {
      logger.error('Update profile error', { message: error.message });
      toast.error(`Failed to update profile: ${error.message}`);
    }
  });
};

/**
 * Update notification preferences
 */
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, preferences }) => {
      const { data, error } = await supabase
        .from('app_users')
        .update({
          metadata: {
            notification_preferences: preferences
          }
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      toast.success('Notification preferences updated!');
    },
    onError: (error) => {
      logger.error('Update preferences error', { message: error.message });
      toast.error(`Failed to update preferences: ${error.message}`);
    }
  });
};

/**
 * Sign out
 */
export const useSignOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.clear();
      window.location.href = '/login';
    },
    onError: (error) => {
      logger.error('Sign out error', { message: error.message });
      toast.error('Failed to sign out');
    }
  });
};

/**
 * Export user data (GDPR compliance)
 */
export const useExportUserData = () => {
  return useMutation({
    mutationFn: async (userId) => {
      // Collect all user data
      const { data: profile } = await supabase
        .from('app_users')
        .select('*')
        .eq('id', userId)
        .single();

      const { data: children } = await supabase
        .from('children')
        .select('*')
        .contains('parent_ids', [userId]);

      const { data: messages } = await supabase
        .from('messages')
        .select('*')
        .eq('sender_id', userId);

      const { data: consents } = await supabase
        .from('consents')
        .select('*')
        .eq('user_id', userId);

      // Create downloadable JSON
      const exportData = {
        profile,
        children,
        messages,
        consents,
        exported_at: new Date().toISOString()
      };

      // Trigger download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kconnect-data-${userId}-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return exportData;
    },
    onSuccess: () => {
      toast.success('Your data has been exported!');
    },
    onError: (error) => {
      logger.error('Export data error', { message: error.message });
      toast.error('Failed to export data');
    }
  });
};

/**
 * Delete user account (GDPR compliance)
 */
export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: async (userId) => {
      // This should be handled by Supabase RLS and CASCADE deletes
      const { error } = await supabase
        .from('app_users')
        .update({ status: 'deleted' })
        .eq('id', userId);

      if (error) throw error;

      // Sign out
      await supabase.auth.signOut();
    },
    onSuccess: () => {
      toast.success('Account deleted. Redirecting...');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    },
    onError: (error) => {
      logger.error('Delete account error', { message: error.message });
      toast.error('Failed to delete account');
    }
  });
};
